import React, { useEffect, useState, useCallback } from "react";
import { getSchools, createSchool, updateSchool, deleteSchool } from "../../api/index";
import { exportAllSchoolsExcel, exportRestrictedSchoolsExcel } from "../exportToExcel";
import AlertSuccess from "../../components/AlertSuccess";
import AlertDenger from "../../components/AlertDenger";
import Button from "../../components/Button";
import UNAUTHORIZED from "../UNAUTHORIZED";

const EMPTY_FORM = { school_name: "", school_city: "", school_country: "Egypt", restricted: false };

function Schools() {
  const userProfile = JSON.parse(localStorage.getItem("profile"))?.value?.[0];
  const isAdmin =
    userProfile?.role === "admin" || userProfile?.username === "abdelrhman";

  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const flash = (type, msg) => {
    if (type === "success") {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(""), 2500);
    } else {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(""), 3000);
    }
  };

  const fetchSchools = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getSchools();
      setSchools(data.schools || []);
    } catch {
      flash("error", "Failed to load schools.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const startEdit = (school) => {
    setEditingId(school.id);
    setForm({
      school_name: school.school_name,
      school_city: school.school_city,
      school_country: school.school_country,
      restricted: !!school.restricted,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.school_name.trim() || !form.school_city.trim()) {
      flash("error", "School name and city are required.");
      return;
    }
    setLoading(true);
    try {
      if (editingId !== null) {
        await updateSchool(editingId, form);
        flash("success", "School updated successfully.");
        setEditingId(null);
      } else {
        await createSchool(form);
        flash("success", "School added successfully.");
      }
      setForm(EMPTY_FORM);
      await fetchSchools();
    } catch {
      flash("error", "Operation failed. Please try again.");
    }
    setLoading(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    setLoading(true);
    try {
      await deleteSchool(id);
      flash("success", "School deleted.");
      await fetchSchools();
    } catch {
      flash("error", "Failed to delete school.");
    }
    setLoading(false);
  };

  const [exporting, setExporting] = useState(false);
  const restrictedCount = schools.filter((s) => s.restricted).length;

  const handleExportAll = async () => {
    if (schools.length === 0) {
      flash("error", "No schools to export.");
      return;
    }
    setExporting(true);
    try {
      await exportAllSchoolsExcel(schools);
    } catch {
      flash("error", "Failed to export Excel file.");
    }
    setExporting(false);
  };

  const handleExportRestricted = async () => {
    if (restrictedCount === 0) {
      flash("error", "No restricted schools to export.");
      return;
    }
    setExporting(true);
    try {
      await exportRestrictedSchoolsExcel(schools);
    } catch {
      flash("error", "Failed to export Excel file.");
    }
    setExporting(false);
  };

  const filtered = schools.filter(
    (s) =>
      s.school_name.toLowerCase().includes(search.toLowerCase()) ||
      s.school_city.toLowerCase().includes(search.toLowerCase()) ||
      s.school_country.toLowerCase().includes(search.toLowerCase())
  );

  if (!isAdmin) return <UNAUTHORIZED />;

  return (
    <div className="p-5">
      <h1 className="text-2xl text-white pb-5 pl-1">Manage Schools</h1>

      {successMsg && <AlertSuccess text={successMsg} />}
      {errorMsg && <AlertDenger text={errorMsg} />}

      {/* Add / Edit form */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">
          {editingId !== null ? "Edit School" : "Add New School"}
        </h2>
        <form onSubmit={handleSubmit} className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col flex-1 min-w-48">
            <label className="text-sm font-medium mb-1">School Name *</label>
            <input
              type="text"
              name="school_name"
              value={form.school_name}
              onChange={handleFormChange}
              placeholder="School name"
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>
          <div className="flex flex-col flex-1 min-w-36">
            <label className="text-sm font-medium mb-1">City *</label>
            <input
              type="text"
              name="school_city"
              value={form.school_city}
              onChange={handleFormChange}
              placeholder="City"
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>
          <div className="flex flex-col flex-1 min-w-36">
            <label className="text-sm font-medium mb-1">Country</label>
            <input
              type="text"
              name="school_country"
              value={form.school_country}
              onChange={handleFormChange}
              placeholder="Country"
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="flex flex-col justify-end min-w-36">
            <label className="flex items-center gap-2 cursor-pointer select-none py-2">
              <input
                type="checkbox"
                name="restricted"
                checked={form.restricted}
                onChange={handleFormChange}
                className="w-4 h-4 accent-red-500"
              />
              <span className="text-sm font-medium">Restricted</span>
            </label>
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={loading}
              color={editingId !== null ? "bg-yellow-500 hover:bg-yellow-600" : "bg-green-500 hover:bg-green-600"}
              text={editingId !== null ? "Update School" : "Add School"}
            />
            {editingId !== null && (
              <Button
                type="button"
                onClick={cancelEdit}
                color="bg-gray-400 hover:bg-gray-500"
                text="Cancel"
              />
            )}
          </div>
        </form>
      </div>

      {/* Search + table */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-xl font-semibold">
            All Schools{" "}
            <span className="text-gray-500 text-base font-normal">({filtered.length})</span>
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleExportAll}
              disabled={exporting || schools.length === 0}
              title="Export all schools — restricted schools highlighted in red"
              className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
            >
              📊 Export All (restricted in red)
            </button>
            <button
              type="button"
              onClick={handleExportRestricted}
              disabled={exporting || restrictedCount === 0}
              title="Export restricted schools only"
              className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
            >
              🚫 Export Restricted ({restrictedCount})
            </button>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, city or country..."
              className="border border-gray-300 rounded-md px-3 py-2 w-72 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        {loading && <p className="text-gray-500 text-center py-6">Loading...</p>}

        {!loading && filtered.length === 0 && (
          <p className="text-gray-400 text-center py-6">No schools found.</p>
        )}

        {!loading && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="px-4 py-2 border border-gray-200">#</th>
                  <th className="px-4 py-2 border border-gray-200">School Name</th>
                  <th className="px-4 py-2 border border-gray-200">City</th>
                  <th className="px-4 py-2 border border-gray-200">Country</th>
                  <th className="px-4 py-2 border border-gray-200 text-center">Restricted</th>
                  <th className="px-4 py-2 border border-gray-200 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((school, idx) => (
                  <tr
                    key={school.id}
                    className={`hover:bg-blue-50 transition-colors ${
                      editingId === school.id ? "bg-yellow-50" : ""
                    }`}
                  >
                    <td className="px-4 py-2 border border-gray-200 text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-2 border border-gray-200 font-medium">{school.school_name}</td>
                    <td className="px-4 py-2 border border-gray-200">{school.school_city}</td>
                    <td className="px-4 py-2 border border-gray-200">{school.school_country}</td>
                    <td className="px-4 py-2 border border-gray-200 text-center">
                      {school.restricted ? (
                        <span className="inline-block px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
                          Restricted
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 border border-gray-200 text-center">
                      <button
                        onClick={() => startEdit(school)}
                        disabled={loading}
                        className="mr-2 px-3 py-1 bg-yellow-400 hover:bg-yellow-500 text-white rounded text-xs font-medium disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(school.id, school.school_name)}
                        disabled={loading}
                        className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-medium disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Schools;
